import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { costsApi } from '@/api/client';
import { formatCurrency, VENTURE_LABELS, VentureName } from '@lytmaster/shared';
import { TrendingUp, TrendingDown, DollarSign, BarChart3, ArrowRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function OverviewPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['costs-overview'],
    queryFn: costsApi.getOverview,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600 font-medium">Failed to load cost data</p>
        <p className="text-red-400 text-sm mt-1">Make sure AWS Cost Explorer is enabled and resources are tagged.</p>
      </div>
    );
  }

  const overview = data;
  const totalCurrent = overview?.totalCurrentMonth || 0;
  const totalPrevious = overview?.totalPreviousMonth || 0;
  const totalChange = totalPrevious ? ((totalCurrent - totalPrevious) / totalPrevious) * 100 : 0;
  const ventures = overview?.ventures || [];
  const forecast = overview?.forecast;

  const pieData = ventures.map((v: any) => ({
    name: VENTURE_LABELS[v.venture as VentureName] || v.venture,
    value: v.currentMonthTotal,
  }));

  const barData = ventures.map((v: any) => ({
    name: VENTURE_LABELS[v.venture as VentureName] || v.venture,
    current: v.currentMonthTotal,
    previous: v.previousMonthTotal,
  }));

  return (
    <div className="space-y-8 fade-in">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Cost Overview</h1>
        <p className="text-slate-500 mt-1">AWS spending across all ventures this month</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <SummaryCard
          label="Current Month"
          value={formatCurrency(totalCurrent)}
          change={totalChange}
          icon={<DollarSign className="w-5 h-5" />}
        />
        <SummaryCard
          label="Previous Month"
          value={formatCurrency(totalPrevious)}
          icon={<BarChart3 className="w-5 h-5" />}
        />
        <SummaryCard
          label="Forecast (EOM)"
          value={forecast ? formatCurrency(forecast.forecastedTotal) : 'N/A'}
          icon={<TrendingUp className="w-5 h-5" />}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Spend by Venture</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                <Bar dataKey="current" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Current" />
                <Bar dataKey="previous" fill="#e2e8f0" radius={[4, 4, 0, 0]} name="Previous" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Cost Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}>
                  {pieData.map((_: any, i: number) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Venture cards */}
      <div>
        <h3 className="font-semibold text-slate-900 mb-4">Ventures</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {ventures.map((v: any) => (
            <button
              key={v.venture}
              onClick={() => navigate(`/ventures/${v.venture}`)}
              className="bg-white rounded-xl border border-slate-200 p-5 text-left hover:shadow-md hover:border-blue-200 transition-all group"
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-slate-900">
                  {VENTURE_LABELS[v.venture as VentureName] || v.venture}
                </h4>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
              </div>
              <div className="text-2xl font-bold text-slate-900">{formatCurrency(v.currentMonthTotal)}</div>
              <div className={`text-sm mt-1 ${v.percentChange > 0 ? 'text-red-500' : 'text-green-600'}`}>
                {v.percentChange > 0 ? '+' : ''}{v.percentChange.toFixed(1)}% vs last month
              </div>
              <div className="mt-3 space-y-1">
                {v.topServices?.slice(0, 3).map((s: any) => (
                  <div key={s.service} className="flex justify-between text-xs text-slate-500">
                    <span className="truncate mr-2">{s.service}</span>
                    <span className="font-medium">{formatCurrency(s.amount)}</span>
                  </div>
                ))}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, change, icon }: {
  label: string;
  value: string;
  change?: number;
  icon: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-slate-500">{label}</span>
        <div className="text-slate-400">{icon}</div>
      </div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      {change !== undefined && (
        <div className={`flex items-center gap-1 text-sm mt-1 ${change > 0 ? 'text-red-500' : 'text-green-600'}`}>
          {change > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {change > 0 ? '+' : ''}{change.toFixed(1)}%
        </div>
      )}
    </div>
  );
}
